import PropTypes from "prop-types";
import {
  Content,
  Text,
  Heading,
  ContextualHelp,
  TagGroup,
  Tag,
  ActionButton,
  LabeledValue,
} from "@react-spectrum/s2";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";

import { ImageAsset } from "@app/components/ImageAsset";
import { ColorAsset } from "@app/components/ColorAsset";
import { resolveCaptionText } from "@app/components/ThumbnailCaption";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * The template assets (`template.data.assets`) by name, each merged with the
 * video's asset of the same name — what the user uploaded or picked for it.
 */
export const getExtendedTemplateAssets = (templateAssets, videoAssets) =>
  (templateAssets ?? []).reduce((obj, asset) => {
    const selectedAsset = videoAssets?.find((a) => a.name === asset.name);

    obj[asset.name] = { ...asset, ...selectedAsset };

    return obj;
  }, {});

/**
 * Asset labels are plain strings, so the optional marker is appended to the
 * string rather than passed as a node. `label` overrides the asset's own.
 */
export const getAssetLabel = (asset, label) =>
  `${label ?? asset?.label ?? asset?.name}${asset?.isOptional ? " (optional)" : ""}`;

/**
 * Whether a use-case applies: its `condition` is evaluated with `video`,
 * `template` and `selectedCampaign` (from `scope`) in scope.
 */
export const isUseCaseVisible = (useCase, scope = {}) => {
  const { video, template, selectedCampaign } = scope;

  if (typeof useCase === "string" || !useCase?.condition) {
    return true;
  }

  try {
    return !!eval(useCase.condition);
  } catch (error) {
    console.warn("Failed to evaluate use-case condition:", useCase, error);
    return false;
  }
};

const getVisibleUseCases = (asset, scope) =>
  (asset?.useCases ?? []).filter((useCase) => isUseCaseVisible(useCase, scope));

const getUseCaseLabel = (useCase) =>
  typeof useCase === "string" ? useCase : useCase?.label;

/** A field's `{{ Campaign-key }}` placeholders, filled from the campaign. */
export const interpolateField = (value, selectedCampaign) =>
  typeof value === "string"
    ? value
        .replace(
          /\{\{\s*([\w.-]+)\s*\}\}/g,
          (_match, key) => selectedCampaign?.[key] ?? "",
        )
        .trim()
    : value;

const scopePropType = PropTypes.shape({
  video: PropTypes.object,
  template: PropTypes.object,
  selectedCampaign: PropTypes.object,
});

const UseCases = ({ asset, scope }) => {
  const useCases = getVisibleUseCases(asset, scope);

  return useCases.length ? (
    <TagGroup label="Use-cases" size="S" selectionMode="none">
      {useCases.map((useCase, uix) => (
        <Tag key={uix}>{getUseCaseLabel(useCase)}</Tag>
      ))}
    </TagGroup>
  ) : null;
};

UseCases.propTypes = {
  asset: PropTypes.object,
  scope: scopePropType,
};

const Product = ({ asset, scope }) =>
  !!asset?.product && (
    <LabeledValue
      label="Product"
      size="S"
      value={
        <Text styles={style({ font: "detail-sm" })}>
          {interpolateField(asset.product, scope?.selectedCampaign)}
        </Text>
      }
    />
  );

Product.propTypes = {
  asset: PropTypes.object,
  scope: scopePropType,
};

/** Whether a template asset has anything for `TemplateAssetHelp` to show. */
export const hasTemplateAssetHelp = (asset, scope) =>
  !!asset?.description ||
  !!asset?.info ||
  getVisibleUseCases(asset, scope).length > 0;

/**
 * A template asset's contextual help — its info (or description), product and
 * use-cases — or `null` when it has none of them.
 */
export const TemplateAssetHelp = ({ asset, label, scope }) =>
  hasTemplateAssetHelp(asset, scope) ? (
    <ContextualHelp variant="info" styles={style({ display: "inline-flex" })}>
      <Heading>{label ?? asset?.label ?? asset?.name}</Heading>
      <Content
        styles={style({ display: "flex", flexDirection: "column", gap: 12 })}
      >
        {!!asset?.info ? (
          <Text>{asset.info}</Text>
        ) : (
          !!asset?.description && <Text>{asset.description}</Text>
        )}
        <Product asset={asset} scope={scope} />
        <UseCases asset={asset} scope={scope} />
      </Content>
    </ContextualHelp>
  ) : null;

TemplateAssetHelp.propTypes = {
  asset: PropTypes.object,
  label: PropTypes.string,
  scope: scopePropType,
};

/**
 * What a template asset's card shows below its preview and description: the
 * `children`, its product and its use-cases.
 */
export const TemplateAssetDetails = ({ asset, scope, children }) => (
  <Content
    styles={style({
      display: "flex",
      flexDirection: "column",
      gap: 12,
    })}
  >
    {children}
    <Product asset={asset} scope={scope} />
    <UseCases asset={asset} scope={scope} />
  </Content>
);

TemplateAssetDetails.propTypes = {
  asset: PropTypes.object,
  scope: scopePropType,
  children: PropTypes.node,
};

/**
 * An image template asset filled by dropping or picking an image, with its
 * contextual help and — unless `showDetails` is off — its description and
 * details, `children` included. `asset` is the template's definition,
 * `selectedAsset` the same one merged with the video's (see
 * `getExtendedTemplateAssets`). `label` and `description` override the asset's
 * own, `null` hiding them (see `resolveCaptionText`); the layout props
 * (`aspectRatio`, `isFluid`, `isCaptioned`, `styles`) are `ImageAsset`'s.
 */
export const TemplateImageAsset = ({
  asset,
  selectedAsset,
  label,
  description,
  scope,
  showDetails = true,
  onDrop,
  onDelete,
  children,
  ...layoutProps
}) => (
  <ImageAsset
    name={asset.name}
    // Deleting an optional asset that was selected, as updating an image
    // variation is, beside the label.
    actions={
      showDetails &&
      !!asset?.isOptional &&
      !!selectedAsset?.filePath &&
      !!onDelete ? (
        <ActionButton
          size="XS"
          aria-label={`Delete ${getAssetLabel(asset, label)}`}
          isDisabled={!!selectedAsset?.isLoading}
          onPress={() => onDelete(selectedAsset)}
        >
          <DeleteIcon />
          <Text>Delete</Text>
        </ActionButton>
      ) : undefined
    }
    label={label === null ? null : getAssetLabel(asset, label)}
    description={resolveCaptionText(
      description,
      showDetails ? asset.description : null,
    )}
    contextualHelp={
      hasTemplateAssetHelp(asset, scope) ? (
        <TemplateAssetHelp asset={asset} label={label} scope={scope} />
      ) : null
    }
    filePath={selectedAsset?.filePath}
    src={selectedAsset?.src}
    isFilled={!!selectedAsset?.filePath}
    isLoading={selectedAsset?.isLoading}
    onDrop={(items, name) => onDrop(items, name)}
    {...layoutProps}
  >
    {showDetails && (
      <TemplateAssetDetails asset={asset} scope={scope}>
        {children}
      </TemplateAssetDetails>
    )}
  </ImageAsset>
);

TemplateImageAsset.propTypes = {
  asset: PropTypes.object.isRequired,
  selectedAsset: PropTypes.object,
  label: PropTypes.string,
  description: PropTypes.string,
  scope: scopePropType,
  showDetails: PropTypes.bool,
  onDrop: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  children: PropTypes.node,
};

/**
 * A color template asset picked from the scraped `colors`, falling back to the
 * customer's color. `asset` is the template's definition, `selectedAsset` the
 * same one merged with the video's. `label` and `description` override the
 * asset's own, `null` hiding them (see `resolveCaptionText`); the remaining
 * props (`isCaptioned`) are `ColorAsset`'s.
 */
export const TemplateColorAsset = ({
  asset,
  selectedAsset,
  label,
  description,
  scope,
  colors = [],
  onChange,
  ...layoutProps
}) => (
  <ColorAsset
    name={asset.name}
    label={label === null ? null : getAssetLabel(asset, label)}
    description={resolveCaptionText(description, asset.description)}
    color={
      (selectedAsset?.color ?? selectedAsset?.value) ||
      scope?.video?.data?.customer?.color ||
      ""
    }
    colors={colors}
    contextualHelp={
      hasTemplateAssetHelp(asset, scope) ? (
        <TemplateAssetHelp asset={asset} label={label} scope={scope} />
      ) : null
    }
    onChange={onChange}
    {...layoutProps}
  />
);

TemplateColorAsset.propTypes = {
  asset: PropTypes.object.isRequired,
  selectedAsset: PropTypes.object,
  label: PropTypes.string,
  description: PropTypes.string,
  scope: scopePropType,
  colors: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};
